// Best-effort client-side page counting for the upload page quota.
//
// The backend is authoritative (it re-counts in stage1 — exactly for PDFs via
// the page tree — and enforces there), so this is purely an optimization: a good
// declared count lets the server reject an over-quota upload *instantly* (429)
// instead of after the file reaches GCS.
//
// We only count what's cheap and reliable in-browser: text/CSV via the char
// convention (must match backend CHARS_PER_PAGE). PDFs and email/PST return null
// — the backend estimates from bytes for the up-front gate and then counts them
// exactly before OCR. Never throws.

const CHARS_PER_PAGE = 3000;

function isCountableText(file: File): boolean {
    if (file.type.startsWith("text/")) return true;
    return /\.(txt|csv|md|log)$/i.test(file.name);
}

/**
 * Declared page count for a file, or null when it can't be determined cheaply
 * client-side (PDF/email/PST/unknown). Null falls back to a server-side estimate
 * + authoritative recount.
 */
export async function computeDeclaredPages(file: File): Promise<number | null> {
    try {
        if (isCountableText(file)) {
            const text = await file.text();
            if (text.length === 0) return 0;
            return Math.max(1, Math.ceil(text.length / CHARS_PER_PAGE));
        }
    } catch {
        // Fall through — backend will estimate + reconcile.
    }
    return null;
}
